/* eslint-disable react/jsx-no-constructed-context-values */
import * as React from 'react';
import axios from 'axios';
import { Snackbar, Alert, Chip, Stack, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import config from '../config';
import { useAuth } from './cognito';
import { VisitStatus } from '../types';

/**
 * Live-collaboration layer for a single territory card (layer1/layer2).
 *
 * - Liveness: short-polls the backend (only while the tab is visible) and
 *   overlays the freshest household statuses on top of the locally-held state.
 * - Presence: sends a heartbeat with "who I am / which household I'm touching",
 *   so co-viewers can see "OO 님 보는 중" and an editing ring on a household.
 * - Conflict safety: toggles use optimistic concurrency (expectedStatus); if
 *   someone else changed the same household first, the server returns 409 and
 *   we reconcile to the server value and notify the user.
 *
 * Transport is plain polling on purpose (robust behind CloudFront + iOS Safari).
 * It can later be swapped for SSE/WebSocket without changing consumers.
 */

interface Viewer {
  user: string;
  display: string;
  editing: number | null;
}

interface LiveCardValue {
  enabled: boolean;
  myId: string;
  viewers: Viewer[];
  /** Number of OTHER people currently viewing this card. */
  othersCount: number;
  /** Server-overridden status for a household, if newer than local. */
  getStatus: (householdId: number) => number | undefined;
  /** Display name of another person currently editing this household, if any. */
  editorOf: (householdId: number) => string | undefined;
  /** Announce that I'm (or am no longer) touching a household. */
  reportEditing: (householdId: number | null) => void;
  /**
   * Toggle a household's visit status with optimistic concurrency.
   * Returns the resulting status and whether a conflict was reconciled.
   */
  toggle: (
    householdId: number,
    fallbackStatus: number,
  ) => Promise<{ status: number; conflict: boolean }>;
}

const noop = () => {};
const DEFAULT: LiveCardValue = {
  enabled: false,
  myId: '',
  viewers: [],
  othersCount: 0,
  getStatus: () => undefined,
  editorOf: () => undefined,
  reportEditing: noop,
  toggle: async (_id, fallbackStatus) => ({
    status: fallbackStatus,
    conflict: false,
  }),
};

const LiveCardContext = React.createContext<LiveCardValue>(DEFAULT);

export const useLiveCard = (): LiveCardValue =>
  React.useContext(LiveCardContext);

/**
 * Small banner showing who else is currently viewing this card.
 * Must be rendered inside a <LiveCardProvider>.
 */
export function LiveViewersBanner() {
  const { viewers, myId, othersCount } = useLiveCard();
  if (othersCount <= 0) return null;
  const others = viewers.filter(v => v.user !== myId);
  const names = others.map(v => v.display).join(', ');
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <PeopleAltIcon fontSize="small" color="action" />
      <Typography variant="body2" color="text.secondary">
        지금 함께 보는 중:
      </Typography>
      <Chip size="small" color="primary" variant="outlined" label={names} />
    </Stack>
  );
}

function nextStatus(current: number): number {
  if (current === VisitStatus.noVisit) return current; // never auto-cycles
  return current === VisitStatus.met ? VisitStatus.intact : current + 1;
}

const POLL_MS = 3000;

// Sentinel householdId the backend sends when a whole card was reset — see
// backend/src/live.ts (recordReset). Clients drop local overlays and refetch.
const RESET_SIGNAL_HOUSEHOLD_ID = 0;

interface ProviderProps {
  layer1?: string;
  layer2?: string;
  /** Called when this card was reset elsewhere, so the host can reload base data. */
  onReset?: () => void;
  children: React.ReactNode;
}

export function LiveCardProvider({
  layer1,
  layer2,
  onReset,
  children,
}: ProviderProps) {
  const { userProfile } = useAuth();
  const display =
    userProfile?.fullKoreanName ||
    userProfile?.name ||
    userProfile?.email ||
    '익명';

  // Presence is keyed per SESSION (tab/device), not per account — so the same
  // login open on two devices counts as two distinct viewers.
  const sessionIdRef = React.useRef<string>('');
  if (!sessionIdRef.current) {
    const account =
      userProfile?.email || userProfile?.['cognito:username'] || 'user';
    sessionIdRef.current = `${account}#${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }
  const myId = sessionIdRef.current;

  const enabled = Boolean(layer1 && layer2);
  const base = `${config.api.url}/mdsouth/live/${layer1}/${layer2}`;

  const [viewers, setViewers] = React.useState<Viewer[]>([]);
  const [overrides, setOverrides] = React.useState<Map<number, number>>(
    new Map(),
  );
  const [conflictOpen, setConflictOpen] = React.useState(false);

  const revRef = React.useRef(0);
  const editingRef = React.useRef<number | null>(null);

  // Keep the latest onReset without re-creating the polling callbacks.
  const onResetRef = React.useRef(onReset);
  onResetRef.current = onReset;

  const applyChanges = React.useCallback(
    (changes: Array<{ householdId: number; status: number }>) => {
      if (!changes.length) return;
      // A reset sentinel invalidates every local overlay; refetch base data.
      if (changes.some(c => c.householdId === RESET_SIGNAL_HOUSEHOLD_ID)) {
        setOverrides(new Map());
        onResetRef.current?.();
        return;
      }
      setOverrides(prev => {
        const next = new Map(prev);
        changes.forEach(c => next.set(c.householdId, c.status));
        return next;
      });
    },
    [],
  );

  const beat = React.useCallback(async () => {
    if (!enabled || document.visibilityState !== 'visible') return;
    try {
      const { data } = await axios.post(`${base}/heartbeat`, {
        user: myId,
        display,
        editing: editingRef.current,
        since: revRef.current,
      });
      if (typeof data?.revision === 'number') revRef.current = data.revision;
      if (Array.isArray(data?.viewers)) setViewers(data.viewers);
      if (Array.isArray(data?.changes)) applyChanges(data.changes);
    } catch (err) {
      // network blips are fine; next tick retries
    }
  }, [enabled, base, myId, display, applyChanges]);

  // Polling loop + visibility wake-up + leave on unmount.
  React.useEffect(() => {
    if (!enabled) return undefined;
    beat();
    const timer = setInterval(beat, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      // best-effort leave
      navigator.sendBeacon?.(
        `${base}/leave`,
        new Blob([JSON.stringify({ user: myId })], {
          type: 'application/json',
        }),
      );
    };
  }, [enabled, beat, base, myId]);

  const reportEditing = React.useCallback(
    (householdId: number | null) => {
      editingRef.current = householdId;
      beat(); // push presence immediately
    },
    [beat],
  );

  const toggle = React.useCallback(
    async (householdId: number, fallbackStatus: number) => {
      const current = overrides.get(householdId) ?? fallbackStatus;
      const target = nextStatus(current);
      try {
        await axios.put(`${config.api.url}/mdsouth/households/${householdId}`, {
          status: target,
          expectedStatus: current,
          layer1,
          layer2,
          byUser: display,
        });
        setOverrides(prev => new Map(prev).set(householdId, target));
        return { status: target, conflict: false };
      } catch (err: any) {
        if (err?.response?.status === 409) {
          const serverStatus = err.response.data?.currentStatus;
          const reconciled =
            typeof serverStatus === 'number' ? serverStatus : current;
          setOverrides(prev => new Map(prev).set(householdId, reconciled));
          setConflictOpen(true);
          return { status: reconciled, conflict: true };
        }
        throw err;
      }
    },
    [overrides, layer1, layer2, display],
  );

  const othersCount = viewers.filter(v => v.user !== myId).length;

  const value: LiveCardValue = {
    enabled,
    myId,
    viewers,
    othersCount,
    getStatus: id => overrides.get(id),
    editorOf: id => {
      const other = viewers.find(v => v.editing === id && v.user !== myId);
      return other?.display;
    },
    reportEditing,
    toggle,
  };

  return (
    <LiveCardContext.Provider value={value}>
      {children}
      <Snackbar
        open={conflictOpen}
        autoHideDuration={3500}
        onClose={() => setConflictOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setConflictOpen(false)}>
          다른 분이 먼저 변경했어요. 최신 상태로 맞췄습니다.
        </Alert>
      </Snackbar>
    </LiveCardContext.Provider>
  );
}
